import axios from "@/utils/axios";
import type { DataCenterSource } from "@/workflow/components/configurations/data-center/types";
import { SOURCE_ORDER } from "@/workflow/components/configurations/data-center/utils";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import type {
  Table,
  PaginatedResponse,
  GetTablesParams,
  ColumnsEditData,
  NewColumnData,
} from "./data-center.types";
import {
  getSourceEndpoint,
  appendSourceToTables,
  buildTableQueryParams,
  EMPTY_PAGINATED_RESPONSE,
} from "./data-center.helpers";

export type {
  TableColumn,
  HolderProcessor,
  Table,
  PaginationLink,
  Meta,
  PaginatedResponse,
  GetTablesParams,
  ColumnsEditData,
  NewColumnData,
} from "./data-center.types";
export {
  useAddDataToTable,
  useUpdateRowData,
  useDeleteRowData,
  useDeleteMultipleRowsData,
  useGetTableData,
  useGetTableDataInfinite,
  getTableData,
  getDataCenterDataById,
  useGetDataCenterDataById,
} from "./data-center.row-data.api";

export const useGetTablesWithSource = (
  source: DataCenterSource,
  params?: GetTablesParams,
) => {
  return useQuery({
    queryKey: ["data-center", "tables", params, source],
    placeholderData: keepPreviousData,
    retry: 0,
    enabled: params?.page !== 0,
    queryFn: async (): Promise<PaginatedResponse<Table>> => {
      const queryParams = buildTableQueryParams(params);
      const search = params?.search;

      if (search && search.trim() !== "") {
        const allResults = await Promise.all(
          SOURCE_ORDER.map(async (sourceType) => {
            try {
              const response = await axios.get(getSourceEndpoint(sourceType), {
                params: queryParams,
              });
              return appendSourceToTables(response.data, sourceType);
            } catch {
              return EMPTY_PAGINATED_RESPONSE(params?.per_page || 20);
            }
          }),
        );

        const combinedData = allResults.flatMap((result) => result.data);
        const totalResults = combinedData.length;
        return {
          data: combinedData,
          links: {
            first: allResults[0]?.links.first || "",
            last: allResults[0]?.links.last || "",
            prev: null,
            next: null,
          },
          meta: {
            current_page: 1,
            from: totalResults > 0 ? 1 : 0,
            last_page: 1,
            links: [],
            path: "",
            per_page: totalResults,
            to: totalResults,
            total: totalResults,
          },
        };
      }

      const response = await axios.get(getSourceEndpoint(source), {
        params: queryParams,
      });
      return appendSourceToTables(response.data, source);
    },
  });
};

export const useGetTables = (params?: GetTablesParams) => {
  return useQuery({
    queryKey: ["data-center", "tables", params],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PaginatedResponse<Table>> => {
      const {
        page,
        per_page,
        filters,
        order_by,
        search,
        role_id,
        orderDirection,
        columns,
      } = params || {};

      const status = filters?.find((obj) => obj.status);
      const updated_at = filters?.find(
        (obj) => obj.updated_at !== undefined,
      )?.updated_at;
      const created_at = filters?.find(
        (obj) => obj.created_at !== undefined,
      )?.created_at;
      const statusValue = status?.status;

      let trashedFilter: string | undefined;

      if (statusValue === "ACTIVE") {
        trashedFilter = "";
      } else if (statusValue === "ACTIVE,INACTIVE") {
        trashedFilter = "with";
      } else if (statusValue === "INACTIVE") {
        trashedFilter = "only";
      }

      const queryParams = {
        ...(page && { page }),
        ...(per_page && { per_page }),
        ...(trashedFilter && { "filter[trashed]": trashedFilter }),
        ...(order_by && {
          order_by: orderDirection === "desc" ? `!${order_by}` : order_by,
        }),
        ...(updated_at && { ["filter[updated_at]"]: updated_at }),
        ...(created_at && { ["filter[created_at]"]: created_at }),
        ...(role_id && { role: role_id }),
        ...(columns !== undefined && { "filter[columns]": columns }),
        filter: { ...(search && { search }) },
      };

      const response = await axios.get("/admin/v1/data-center/tables", {
        params: queryParams,
      });
      return response.data;
    },
  });
};

// Crear una tabla
export const useCreateTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      table_display_name?: string;
      description?: string;
      is_portal_enabled_for_creation?: boolean;
    }) => {
      const response = await axios.post("/admin/v1/data-center/tables", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useGetTableColumnTypes = () => {
  return useQuery({
    queryKey: ["data-center", "table-column-types"],
    queryFn: async () => {
      const response = await axios.get(
        "/admin/v1/data-center/tables/columns-types",
      );
      return response.data;
    },
  });
};

// Actualizar una tabla (por id)
export const useUpdateTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      table_display_name,
      description,
      columns,
      is_portal_enabled_for_creation,
    }: {
      id: string;
      name: string;
      table_display_name?: string;
      description?: string;
      columns?: ColumnsEditData[];
      is_portal_enabled_for_creation?: boolean;
    }) => {
      const response = await axios.patch(`/admin/v1/data-center/tables/${id}`, {
        name,
        table_display_name,
        ...(description && { description }),
        is_portal_enabled_for_creation,
        columns,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

//Agregar nueva columna
export const useAddNewColumnInTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      columns,
    }: {
      id: string;
      name: string;
      description?: string;
      columns: NewColumnData[];
    }) => {
      const response = await axios.patch(`/admin/v1/data-center/tables/${id}`, {
        name,
        ...(description && { description }),
        columns,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Remover una tabla por id específico
export const useRemoveTableById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await axios.delete(
        `/admin/v1/data-center/tables/${id}`,
        { data: { reason } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useReactivateTableById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/${id}/active-table`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Obtener los tipos de columnas aceptados
export const useGetColumnTypes = () => {
  return useQuery({
    queryKey: ["data-center", "columns-types"],
    queryFn: async () => {
      const response = await axios.get(
        "/admin/v1/data-center/tables/columns-types",
      );
      return response.data;
    },
  });
};

export const getTableDetailsByTableName = async (
  name: string,
  source?: string,
) => {
  if (source && source !== "SDT") {
    const response = await axios.get<{
      data: {
        data: Array<ColumnsEditData>;
      };
    }>(`/admin/v1/data-center/functional-core/columns`, {
      params: {
        table_name: name,
      },
    });
    return response.data;
  } else {
    const response = await axios.get<{
      data: {
        columns: Array<ColumnsEditData>;
      };
    }>(`/admin/v1/data-center/tables/byName/${name}`);
    return response.data;
  }
};

export const useGetTableDetailsByTableName = (
  name: string,
  source?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["data-center", "table-details", name, source],
    queryFn: () => getTableDetailsByTableName(name, source),
    enabled: !!name && enabled,
  });
};

export const useGetTableDetail = (
  table: Table | undefined,
  useName: boolean = false,
) => {
  const source = table?.source || "sdt";
  return useQuery({
    queryKey: [
      "data-center",
      "table",
      source === "sdt" ? table?.id : table?.name,
    ],
    queryFn: async () => {
      if (source === "sdt") {
        const response = await axios.get(
          `/admin/v1/data-center/tables/${useName ? "byName/" : ""}${table?.id}`,
        );
        return response.data;
      } else {
        const response = await axios.get<{
          data: Array<{ name: string; type: string }>;
        }>(
          `admin/v1/data-center/sources/${source.toUpperCase()}/tables/columns`,
          {
            params: {
              table_name: table?.name,
            },
          },
        );
        return {
          data: {
            columns: response.data.data.map((item) => ({
              ...item,
              id: item.name,
            })),
            description: "",
            name: table?.name,
          },
        };
      }
    },
    enabled: source === "sdt" ? !!table?.id : !!table?.name,
  });
};

// Mostrar la info de una tabla por id
export const useGetTableById = (id: string) => {
  return useQuery({
    queryKey: ["data-center", "table", id],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/data-center/tables/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const createTableDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const tableId = id;
    try {
      const table = await queryClient.ensureQueryData({
        queryKey: ["data-center", "table", tableId],
        queryFn: async () => {
          const response = await axios.get(
            `/admin/v1/data-center/tables/${tableId}`,
          );
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutos
      });
      return { data: table.data as Table };
    } catch (error) {
      console.error("Error fetching table for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useGetDataCenterTablesInfinite = (searchString?: string) => {
  return useInfiniteQuery({
    queryKey: ["data-center-tables-infinite", searchString],
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async ({ pageParam }): Promise<PaginatedResponse<Table>> => {
      const response = await axios.get<PaginatedResponse<Table>>(
        "/admin/v1/data-center/tables",
        {
          params: {
            page: pageParam,
            per_page: 15,
            order_by: "name",
            "filter[columns]": 1,
            ...(searchString && { "filter[search]": searchString }),
          },
        },
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta.current_page;
      const lastPageNum = lastPage.meta.last_page;
      return currentPage < lastPageNum ? currentPage + 1 : undefined;
    },
  });
};
